import React, { useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';

import {
  PageTitle,
  Button,
  Input,
  Shimmer,
  ShareCommentsPanel,
  ShareApprovalsPanel,
  ShareAnalyticsPanel,
  CampaignLinksModal,
  Dropdown,
} from 'src/components';
import useModal from 'src/hooks/useModal';
import {
  ShareSettingsForm,
  ShareSettings,
  defaultShareSettings,
  shareSettingsFromOptions,
  buildDownloadOptions,
  buildPreviewOptions,
  buildBranding,
} from 'src/components/ShareSettingsForm';
import { useShareQuery } from 'src/api/queries/shareQueries';
import { useUpdateShareMutation, useDeleteShareMutation } from 'src/api/mutations/shareMutations';
import { savePrintExport } from 'src/api/functions/addonsApi';
import { getJobStatus } from 'src/api/functions/assetsApi';
import {
  ShareAsset,
  getShareTitle,
  getShareExpiry,
  getShareFileIds,
  getShareDescription,
} from 'src/types/TShare';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import { usePrintExportAvailable } from 'src/hooks/usePrintExportAvailable';
import { useDeliveryInsightsEnabled } from 'src/hooks/useDeliveryInsightsEnabled';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { getThumbnailUrl, getODIThumbnailUrl } from 'src/utils/previews';
import {
  CaretLeft,
  AssetGetLink,
  Pencil,
  Trash,
  Eye,
  Checks,
  X,
} from 'src/common/icons';

import styles from './ShareDetails.module.scss';

type ShareDetailsParams = {
  id: string;
};

type TabType = 'details' | 'comments' | 'approvals' | 'analytics';

// Minimum expiry date is tomorrow
const getMinExpiryDate = () => moment().add(1, 'day').format('YYYY-MM-DD');
// Default expiry is 1 week from today
const getDefaultExpiryDate = () => moment().add(1, 'week').format('YYYY-MM-DD');

export const ShareDetails: React.FC = () => {
  const { id } = useParams<ShareDetailsParams>();
  const history = useHistory();
  const accessToken = useAccessToken();
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { showModal } = useModal();
  const printExportAvailable = usePrintExportAvailable();
  const deliveryInsightsEnabled = useDeliveryInsightsEnabled();

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('details');

  // Edit form state (share-level fields)
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editExpiry, setEditExpiry] = useState(getDefaultExpiryDate());
  const [editPassword, setEditPassword] = useState('');
  const [editAddEmails, setEditAddEmails] = useState('');

  // Shared branding / preview / download settings
  const [settings, setSettings] = useState<ShareSettings>(defaultShareSettings());

  // Print-ready "generate the rest"
  const [generatingPrint, setGeneratingPrint] = useState(false);

  // Query
  const { data: shareResult, isLoading, refetch } = useShareQuery(id);

  // Mutations
  const updateShareMutation = useUpdateShareMutation(id, {
    onSuccess: () => {
      showSuccess('Share updated successfully');
      setIsEditing(false);
      refetch();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const deleteShareMutation = useDeleteShareMutation({
    onSuccess: () => {
      showSuccess('Share deleted successfully');
      history.push('/shares');
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  // Derived data
  const share = shareResult?.data;
  const title = share ? getShareTitle(share) : '';
  const description = share ? getShareDescription(share) : '';
  const expiry = share ? getShareExpiry(share) : undefined;
  const assetIds = share ? getShareFileIds(share) : [];
  const assets = share?.assets || [];

  const shareUrl = useMemo(() => {
    if (!share) return '';
    return `${window.location.origin}/shared/${share.id}`;
  }, [share]);

  // Print-ready download availability across this share's image assets. A share
  // only serves the print_ready conversion for assets that already have it, so
  // the conversion appears in shared_links only when present.
  const printReadyEnabled = !!share?.download_options?.image?.print_ready?.enabled;
  const printReadyConversionName =
    share?.download_options?.image?.print_ready?.conversion_name || 'print_ready';
  const imageAssets = useMemo(
    () => assets.filter((a) => (a.content_type || '').startsWith('image')),
    [assets],
  );
  const assetsMissingPrintReady = useMemo(
    () =>
      imageAssets.filter(
        (a) =>
          !(a.shared_links?.conversions || []).some(
            (c) => c.key === printReadyConversionName,
          ),
      ),
    [imageAssets, printReadyConversionName],
  );
  const printReadyAvailableCount = imageAssets.length - assetsMissingPrintReady.length;

  const pollPrintJob = async (jobId: string): Promise<void> => {
    for (let attempt = 0; attempt < 10; attempt++) {
      await new Promise((r) => setTimeout(r, 2000));
      const job: any = await getJobStatus(jobId, accessToken);
      if (job?.status === 'COMPLETED' || job?.status === 'ERROR') return;
    }
  };

  const handleGeneratePrintReady = async () => {
    if (!printExportAvailable) {
      showAlert('The Print Export addon is not enabled for your account.');
      return;
    }
    setGeneratingPrint(true);
    let failed = 0;
    try {
      for (const asset of assetsMissingPrintReady) {
        const res = await savePrintExport(
          asset.id,
          { format: 'tiff', dpi: 300 },
          'add_conversion',
          { conversion_name: printReadyConversionName },
          accessToken,
        );
        if (!res.ok) {
          failed += 1;
          const status = res.error?.status;
          if (status === 402) {
            showAlert(
              res.error?.data?.error === 'insufficient_credits'
                ? 'Not enough credits to generate print-ready files.'
                : 'Print Export quota exhausted.',
            );
            break; // stop on quota/credit errors
          }
          if (status === 403) {
            showAlert('The Print Export addon is not enabled for your account.');
            break;
          }
          continue;
        }
        if (res.jobId) {
          // eslint-disable-next-line no-await-in-loop
          await pollPrintJob(res.jobId);
        }
      }
      if (failed === 0) {
        showSuccess('Print-ready files generated');
      }
      refetch();
    } finally {
      setGeneratingPrint(false);
    }
  };

  const getStatusClass = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'OK':
      case 'ACTIVE':
        return styles.active;
      case 'EXPIRED':
        return styles.expired;
      case 'DISABLED':
        return styles.disabled;
      default:
        return '';
    }
  };

  const getDisplayStatus = (status: string): string => {
    if (status?.toUpperCase() === 'OK') return 'Active';
    return status || '-';
  };

  const formatExpiryDate = (): string => {
    // Use expiry_utc directly if available (ISO 8601 format)
    if (share?.expiry_utc) {
      return moment.utc(share.expiry_utc).local().format('MMMM D, YYYY');
    }
    // Fallback to computed expiry (Unix timestamp)
    if (expiry) {
      return moment.unix(expiry).format('MMMM D, YYYY');
    }
    return 'Never';
  };

  const handleStartEdit = () => {
    if (!share) return;
    setEditTitle(title);
    setEditDescription(description || '');
    // Initialize with current expiry if exists, otherwise default to 1 week from today
    if (share.expiry_utc) {
      setEditExpiry(moment.utc(share.expiry_utc).local().format('YYYY-MM-DD'));
    } else if (expiry) {
      setEditExpiry(moment.unix(expiry).format('YYYY-MM-DD'));
    } else {
      setEditExpiry(getDefaultExpiryDate());
    }
    // Populate access control from share
    setEditPassword(share.access_control?.password || share.params?.access_control?.password || '');
    setEditAddEmails('');

    // Populate branding / preview / download settings from share
    setSettings(
      shareSettingsFromOptions({
        branding: share.branding,
        downloadOptions: share.download_options,
        previewOptions: share.preview_options,
        allowFeedback: share.allow_feedback,
        allowApproval: share.allow_approval,
      }),
    );

    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    const newEmailList = editAddEmails
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    updateShareMutation.mutate({
      title: editTitle,
      description: editDescription || undefined,
      password: editPassword || null,
      expiry: editExpiry,
      allow_feedback: settings.allowFeedback,
      allow_approval: settings.allowApproval,
      branding: buildBranding(settings),
      download_options: buildDownloadOptions(settings),
      preview_options: buildPreviewOptions(settings),
      add_emails: newEmailList.length > 0 ? newEmailList : undefined,
    });
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('Share link copied to clipboard');
    } catch (err) {
      showAlert('Failed to copy link');
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this share? This action cannot be undone.')) {
      deleteShareMutation.mutate(id);
    }
  };

  const handleGoBack = () => {
    history.push('/shares');
  };

  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.loadingWrapper}>
          <Shimmer w="100%" h="4rem" radius="0.5rem" />
          <Shimmer w="100%" h="20rem" radius="0.5rem" />
        </div>
      </div>
    );
  }

  if (!share) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.errorState}>
          <h3>Share not found</h3>
          <p>The share you're looking for doesn't exist or has been deleted.</p>
          <Button onClick={handleGoBack}>Back to Shares</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleGoBack}>
          <CaretLeft />
          <span>Back to Shares</span>
        </button>

        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <h1 className={styles.shareTitle}>{title}</h1>
            <span className={cn(styles.status, getStatusClass(share.status))}>
              {getDisplayStatus(share.status)}
            </span>
          </div>

          <div className={styles.headerActions}>
            {!isEditing && (
              <>
                <Button
                  mode="tertiary"
                  size="small"
                  icon={<Pencil />}
                  onClick={handleStartEdit}
                >
                  Edit
                </Button>
                <Button
                  mode="tertiary"
                  size="small"
                  icon={<AssetGetLink />}
                  disabled={!deliveryInsightsEnabled}
                  onClick={() => showModal(<CampaignLinksModal mode="share" shareId={id} />)}
                >
                  Campaign links
                </Button>
                <Button
                  mode="tertiary"
                  size="small"
                  icon={<Trash />}
                  onClick={handleDelete}
                  className={styles.deleteButton}
                >
                  Delete
                </Button>
              </>
            )}
            {isEditing && (
              <>
                <Button
                  mode="tertiary"
                  size="small"
                  icon={<X />}
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  mode="primary"
                  size="small"
                  icon={<Checks />}
                  onClick={handleSaveEdit}
                  loading={updateShareMutation.isLoading}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Share URL Section */}
      <div className={styles.urlSection}>
        <label className={styles.urlLabel}>Share Link</label>
        <div className={styles.urlContainer}>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.urlInput}
            title={shareUrl}
          >
            {shareUrl} <span className={styles.urlHint}>(click to open in new tab)</span>
          </a>
          <Button
            mode="primary"
            size="small"
            icon={<AssetGetLink />}
            onClick={handleCopyUrl}
          >
            Copy Link
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabsContainer}>
        <button
          className={cn(styles.tab, { [styles.active]: activeTab === 'details' })}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={cn(styles.tab, { [styles.active]: activeTab === 'comments' })}
          onClick={() => setActiveTab('comments')}
          disabled={!share.allow_feedback}
        >
          Comments
        </button>
        <button
          className={cn(styles.tab, { [styles.active]: activeTab === 'approvals' })}
          onClick={() => setActiveTab('approvals')}
          disabled={!share.allow_approval}
        >
          Approvals
        </button>
        <button
          className={cn(styles.tab, { [styles.active]: activeTab === 'analytics' })}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === 'details' && (
          <div className={styles.detailsContent}>
            {/* Info Section */}
            <div className={styles.infoSection}>
              {isEditing ? (
                // Edit Form
                <div className={styles.editForm}>
                  {/* Title and Expiry Row */}
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <Input
                        label="Title"
                        value={editTitle}
                        onChange={setEditTitle}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <div className={styles.dateInputWrapper}>
                        <label>
                          <p>Expiry Date</p>
                          <input
                            type="date"
                            className={styles.dateInput}
                            value={editExpiry}
                            onChange={(e) => setEditExpiry(e.target.value)}
                            min={getMinExpiryDate()}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <Input
                        label="Description"
                        value={editDescription}
                        onChange={setEditDescription}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <Input
                        label="Password (optional, leave empty to remove password protection)"
                        value={editPassword}
                        onChange={setEditPassword}
                        type="password"
                      />
                    </div>
                  </div>

                  {/* Shared With / Add Recipients */}
                  <div className={styles.addEmailsSection}>
                    {share.shared_with && share.shared_with.length > 0 && (
                      <div className={styles.existingEmails}>
                        <span className={styles.existingEmailsLabel}>Current Recipients</span>
                        <div className={styles.emailTags}>
                          {share.shared_with.map((email) => (
                            <span key={email} className={styles.emailTag}>{email}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className={styles.formGroup}>
                      <Input
                        label="Add Recipients"
                        placeholder="Enter email addresses (comma-separated)"
                        value={editAddEmails}
                        onChange={setEditAddEmails}
                      />
                      <p className={styles.formHint}>
                        New recipients will receive an email notification with the share link
                      </p>
                    </div>
                  </div>

                  {/* Branding / Preview / Download controls (shared form) */}
                  <ShareSettingsForm
                    value={settings}
                    onChange={setSettings}
                    showPrintReady={printExportAvailable}
                  />
                </div>
              ) : (
                // View Mode
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Description</span>
                    <span className={styles.infoValue}>
                      {description || 'No description'}
                    </span>
                  </div>

                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Expiry Date</span>
                    <span className={styles.infoValue}>{formatExpiryDate()}</span>
                  </div>

                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Assets</span>
                    <span className={styles.infoValue}>
                      {assetIds.length} asset{assetIds.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Allow Feedback</span>
                    <span className={styles.infoValue}>
                      {share.allow_feedback ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Allow Approval</span>
                    <span className={styles.infoValue}>
                      {share.allow_approval ? 'Yes' : 'No'}
                    </span>
                  </div>

                  {share.shared_with && share.shared_with.length > 0 && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Shared With</span>
                      <div className={styles.emailTags}>
                        {share.shared_with.map((email) => (
                          <span key={email} className={styles.emailTag}>{email}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {share.branding && (
                    <>
                      {share.branding.logo_url && (
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Logo</span>
                          <div className={styles.logoPreview}>
                            <img
                              src={share.branding.logo_url}
                              alt="Share logo"
                            />
                          </div>
                        </div>
                      )}
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Colors</span>
                        <div className={styles.brandingPreview}>
                          <div
                            className={styles.colorSwatch}
                            style={{ backgroundColor: share.branding.primary_color }}
                            title={`Primary: ${share.branding.primary_color}`}
                          />
                          <div
                            className={styles.colorSwatch}
                            style={{ backgroundColor: share.branding.background_color }}
                            title={`Background: ${share.branding.background_color}`}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Print-ready availability + generate the rest */}
            {!isEditing && printReadyEnabled && imageAssets.length > 0 && (
              <div className={styles.printReadyPanel}>
                <div className={styles.printReadyInfo}>
                  <h3 className={styles.sectionTitle}>Print-ready downloads</h3>
                  <p className={styles.printReadyStatus}>
                    Print-ready available for {printReadyAvailableCount} of{' '}
                    {imageAssets.length} image asset
                    {imageAssets.length !== 1 ? 's' : ''}.
                  </p>
                  {assetsMissingPrintReady.length > 0 && !printExportAvailable && (
                    <p className={styles.printReadyHint}>
                      Add the Print Export addon to generate print files for the
                      remaining assets.
                    </p>
                  )}
                </div>
                {assetsMissingPrintReady.length > 0 && printExportAvailable && (
                  <Button
                    mode="primary"
                    size="small"
                    onClick={handleGeneratePrintReady}
                    loading={generatingPrint}
                    disabled={generatingPrint}
                  >
                    Generate the rest ({assetsMissingPrintReady.length})
                  </Button>
                )}
              </div>
            )}

            {/* Assets Section */}
            <div className={styles.assetsSection}>
              <h3 className={styles.sectionTitle}>
                Assets ({assetIds.length})
              </h3>

              {assetIds.length === 0 ? (
                <p className={styles.noAssets}>No assets in this share</p>
              ) : (
                <div className={styles.assetsGrid}>
                  {(assets.length > 0 ? assets : assetIds.map(id => ({ id }))).map((asset: ShareAsset | { id: string }) => {
                    // Use ODI thumbnail URL if shared_links is available, otherwise fall back to authenticated URL
                    const thumbnailUrl = 'shared_links' in asset && asset.shared_links?.odi
                      ? getODIThumbnailUrl({ odi: asset.shared_links.odi, width: 200, height: 200 })
                      : getThumbnailUrl({
                          width: 200,
                          height: 200,
                          id: asset.id,
                          jwt: accessToken,
                        });
                    
                    const assetName = 'name' in asset ? asset.name : undefined;
                    
                    return (
                      <div key={asset.id} className={styles.assetCard}>
                        <a
                          href={`/asset/${asset.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.assetLink}
                        >
                        <img
                          src={thumbnailUrl}
                          alt={assetName || 'Asset'}
                          className={styles.assetThumbnail}
                        />
                        </a>
                        {assetName && (
                          <span className={styles.assetName} title={assetName}>
                            {assetName}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'comments' && (
          <ShareCommentsPanel shareId={id} />
        )}

        {activeTab === 'approvals' && (
          <ShareApprovalsPanel shareId={id} assets={assets} />
        )}

        {activeTab === 'analytics' && (
          <ShareAnalyticsPanel shareId={id} assets={assets} />
        )}
      </div>
    </div>
  );
};

export default ShareDetails;

